import {
  GridActionsCellItem,
  GridColumns,
  GridNativeColTypes,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from '@mui/x-data-grid-pro'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { QueryKeyT } from '@lovoldsystem/core.client'
import { GenericToggle } from '../../../components/GenericToggle'
import { CustomStyledTable } from 'src/screens/localities/moorings/Components/PositionTable'
import { Flex } from 'src/styles/flexComponent'
import { Badge, styled, Tooltip } from '@mui/material'
import { People } from '@mui/icons-material'
import { ReactComponent as LocalityIcon } from 'src/assets/icons/localityIcon.svg'
import { DialogContextType } from 'src/enums'

interface LocalityGroupsTableProps {
  getDetailPanelContent?: (params: any) => JSX.Element
  queryUrl: string
  generateQueryKey?: (page: number, pageSize: number) => QueryKeyT | undefined
  queryParams?: object
  onUpdate?: (id: EntityId) => void
  onDelete?: (id: EntityId) => void
}

export const LocalityGroupsTable: React.FC<LocalityGroupsTableProps> = ({
  onDelete,
  onUpdate,
  queryUrl,
  queryParams,
  generateQueryKey,
  getDetailPanelContent,
}) => {
  const { t } = useTranslation(['common'])

  const renderActions = useCallback(
    ({ row, id }) => {
      const actions: JSX.Element[] = []
      if (onUpdate) {
        actions.push(
          <GridActionsCellItem
            key={row.id}
            label={t('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate && onUpdate(row.id)}
            color="primary"
            showInMenu
          />,
        )
      }
      if (onDelete) {
        actions.push(
          <GridActionsCellItem
            key={id}
            label={t('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => {
              onDelete && onDelete(row.id)
            }}
            color="primary"
            showInMenu
          />,
        )
      }
      return actions
    },
    [onUpdate],
  )

  const STATIC_COLUMNS: GridColumns = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      align: 'center',
      renderCell: params => (
        <GenericToggle id={params.id} value={params.value} expandTitle={t('view-locality-group', { ns: 'locality' })} />
      ),
    },
    { field: 'name', headerName: t('name', { ns: 'common' }), flex: 1 },
    {
      field: 'count',
      headerName: '',
      flex: 1,
      renderCell: params => {
        return (
          <Flex.Row justifyContent={'flex-end'} flex={1} columnGap={5} paddingRight={2}>
            <Tooltip title={t('localities', { ns: 'common' }) || ''}>
              <Badge badgeContent={params.row.localityCount} color="primary" sx={{ marginTop: '10px' }}>
                <LocalityIconStyled />
              </Badge>
            </Tooltip>
            <Tooltip title={t('users', { ns: 'users' }) || ''}>
              <Badge badgeContent={params.row.usersCount} color="primary" sx={{ marginTop: '10px' }}>
                <People />
              </Badge>
            </Tooltip>
          </Flex.Row>
        )
      },
    },
  ]

  const columns = [
    ...STATIC_COLUMNS,
    {
      field: 'actions',
      type: 'actions' as GridNativeColTypes,
      headerName: '',
      width: 60,
      getActions: renderActions,
    },
  ]

  return (
    <CustomStyledTable
      queryUrl={queryUrl}
      queryParams={queryParams}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
      getRowHeight={() => 'auto'}
      columns={columns.filter(col => onUpdate || col.field !== 'actions')}
      id={'locality-groups-table'}
      enableToolbar={true}
      enableServerPagination
      generateQueryKey={generateQueryKey}
      autoHeight={false}
      height={window.innerHeight - 230}
      contextType={DialogContextType.customerAdminContext}
    />
  )
}

export const LocalityIconStyled = styled(LocalityIcon)`
  height: 30px;
  margin-top: -5px;
  margin-left: 5px;
`
